import { useEffect, useState } from "react";

import { FileAttachment as Base } from "@corca/tiptap";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { useTranslations } from "next-intl";

import { attachUpload, disposeApi, getFileSignedUrl } from "@/api";
import { Icon } from "@/components/icons/Icon";
import { FileCard } from "@/components/tiptap/FileCard";
import { LineWrapper } from "@/components/tiptap/utils/line-wrapper/LineWrapper";
import { useUploadScopes } from "@/components/tiptap/utils/useUploadScopes";
import { getExtension } from "@/models/utils/getFileIconName";
import { downloadFromUrl } from "@/utils/downloadFile";
import { formatBytes } from "@/utils/formatBytes";
import { twMerge } from "@/utils/twMerge";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";
import type { DocumentModel } from "@/models/DocumentModel";
import type { FileAttachmentNodeAttrs } from "@corca/tiptap";
import type { NodeViewProps } from "@tiptap/core";

type FileAttachmentExtensionOptions = {
  doc: DocumentModel;
  uploadScope?: UploadScope;
};

const FileAttachmentWrapper = ({
  node,
  selected,
  extension,
  getPos,
  editor,
  updateAttributes,
}: NodeViewProps) => {
  const { doc, uploadScope } = extension.options as FileAttachmentExtensionOptions;
  const t = useTranslations();

  const { key, filename, contentType, byteSize, sourceUid, sourceType } =
    node.attrs as FileAttachmentNodeAttrs;

  const { scope, sourceScope, resolveScope } = useUploadScopes(doc, uploadScope, {
    sourceUid,
    sourceType,
  });
  const [downloading, setDownloading] = useState(false);

  const name = filename ?? "";
  const extensionName = getExtension(name);

  useEffect(() => {
    return disposeApi(async (signal) => {
      if (!scope || !sourceScope || !key || !editor.isEditable) return;

      try {
        await attachUpload(
          scope.type,
          scope.uid,
          key,
          { sourceUid: sourceScope.uid, sourceType: sourceScope.type },
          { signal },
        );
        if (signal.aborted) return;
        updateAttributes({ sourceUid: null, sourceType: null });
      } catch (error) {
        console.error("Failed to attach pasted file", error);
      }
    });
  }, [key, scope, sourceScope, updateAttributes, editor.isEditable]);

  const handleDownload = async () => {
    if (!resolveScope || !key || downloading) {
      return;
    }

    setDownloading(true);
    try {
      const { signedUrl } = await getFileSignedUrl(resolveScope.type, resolveScope.uid, key);
      downloadFromUrl(signedUrl, name, contentType ?? "application/octet-stream");
    } catch (error) {
      console.error("Failed to download file", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <NodeViewWrapper>
      <LineWrapper
        doc={doc}
        getPos={getPos}
        className="group"
        contentClassName="min-w-0"
        rowClassName="items-center"
      >
        <FileCard
          filename={name}
          contentType={contentType ?? ""}
          selected={selected}
          actions={
            <button
              type="button"
              aria-label={t("doc.file.download-label")}
              disabled={downloading}
              className={twMerge(
                "shrink-0 flex items-center justify-center w-8 h-8 rounded-8",
                "hover:bg-gray-100 dark:hover:bg-gray-800",
                downloading && "opacity-50 cursor-default",
              )}
              onClick={handleDownload}
            >
              <Icon name="download/Download" width={24} height={24} />
            </button>
          }
        >
          <span className="hidden @min-[256px]:inline whitespace-nowrap text-secondary">
            {extensionName && (
              <>
                <span> · </span>
                <span>{extensionName.toUpperCase()}</span>
              </>
            )}
            {byteSize !== null && (
              <>
                <span> · </span>
                <span>{formatBytes(byteSize)}</span>
              </>
            )}
          </span>
        </FileCard>
      </LineWrapper>
    </NodeViewWrapper>
  );
};

export const FileAttachmentPlugin = Base.extend<FileAttachmentExtensionOptions>({
  addNodeView() {
    return ReactNodeViewRenderer(FileAttachmentWrapper, {
      className: "w-full mx-auto max-w-content-width sm:max-w-full",
    });
  },
});
